import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Toaster, toast } from 'react-hot-toast'
import AuthHeader from '../../components/company/registration/AuthHeader'
import RegistrationSidebar from '../../components/company/registration/RegistrationSidebar'
import StepIndicator from '../../components/company/registration/StepIndicator'
import CompanyProfileBuilding from './steps/CompanyProfileBuilding'
import { companyService } from '../../services/companyService'
import { authService } from '../../services/authService'
import { companyRegistrationSteps } from '../../utils/companyRegistrationSteps'
import { mapCompanyProfileToForm } from '../../utils/dataMapping'

const ProfileBuildingPage = () => {
    const navigate = useNavigate()
    const formRef = React.useRef(null)
    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState({})
    const [userId, setUserId] = useState(null)
    const [companyId, setCompanyId] = useState(null)

    // Current Step: 2
    const currentStepConfig = companyRegistrationSteps.find(s => s.id === 2)

    useEffect(() => {
        const checkStatus = async () => {
            try {
                const currentUser = await authService.getCurrentUser()
                if (!currentUser) {
                    navigate('/auth/login')
                    return
                }
                setUserId(currentUser.id || currentUser.user_id)

                // Get companyId
                const storedCompanyId = sessionStorage.getItem('companyId') || currentUser.company_id
                setCompanyId(storedCompanyId)

                // Check registration status
                const status = currentUser.hasCompletedRegistration

                // Logic based on status
                if (status === true || status === 'true') {
                    // navigate('/company/registration/documents')
                }

                // Allow loading if company-profile is done (edit mode)

                if (!status || status === 'false') {
                    // If step 1 not done, maybe redirect? 
                    // But if they have companyId, they might have skipped updates.
                    // Generally good to ensure they have Step 1 done.
                    // But let's allow access if companyId exists.
                    if (!storedCompanyId) {
                        navigate('/company/registration/legal-details')
                        return
                    }
                }

                // Load existing data if any
                if (storedCompanyId) {
                    try {
                        const data = await companyService.getCompanyProfileData(currentUser.id || currentUser.user_id)
                        if (data) {
                            setFormData(mapCompanyProfileToForm(data))
                        }
                    } catch (e) {
                        // Ignore if no profile data yet
                    }
                }

            } catch (error) {
                console.error("Status check failed", error)
            }
        }
        checkStatus()
    }, [navigate])

    const handleNext = async (stepData) => {
        try {
            setIsLoading(true)

            let response;
            // Always use saveProfileDetails (POST)
            response = await companyService.saveProfileDetails(stepData)

            // Update user status
            const user = await authService.getCurrentUser()
            if (user) {
                // Update status to 'company-profile' to indicate Step 2 is done, unlocking Step 3 (Subscription)
                const updatedUser = { ...user, hasCompletedRegistration: 'company-profile' }
                sessionStorage.setItem('userData', JSON.stringify(updatedUser))
                sessionStorage.setItem('hasCompletedRegistration', JSON.stringify('company-profile'))
            }

            // localStorage.setItem('company_registration_step2', JSON.stringify(stepData))

            toast.success('Company profile saved successfully!')
            navigate('/company/registration/subscription')

        } catch (error) {
            console.error(error)
            toast.error(error.message || 'Failed to save profile')
        } finally {
            setIsLoading(false)
        }
    }

    const handlePrevious = () => {
        navigate('/company/registration/legal-details')
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
            <AuthHeader />
            <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-4 gap-4">
                {/* Step Indicator */}
                <div className="w-full max-w-6xl">
                    <StepIndicator currentStep={2} totalSteps={companyRegistrationSteps.length} />
                </div>

                <div className="flex items-start justify-center w-full gap-4">
                    {/* Sidebar */}
                    <div className="hidden lg:block">
                        <RegistrationSidebar currentStep={2} steps={companyRegistrationSteps} />
                    </div>

                    {/* Main Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card w-full max-w-6xl p-5 md:p-6 shadow-glow flex-1 bg-white/80 dark:bg-slate-800/90 backdrop-blur-xl border border-secondary-200 dark:border-secondary-700 rounded-2xl"
                    >
                        {/* Header */}
                        <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
                            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
                                {currentStepConfig?.title || 'Profile Building'}
                            </h1>
                            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
                                {currentStepConfig?.description || 'Company profile and branding'}
                            </p>
                        </div>

                        <div className="mt-4">
                            <CompanyProfileBuilding
                                ref={formRef}
                                formData={formData}
                                onNext={handleNext}
                                onPrevious={handlePrevious}
                                isLoading={isLoading}
                                userId={userId}
                                companyId={companyId}
                            />
                        </div>

                        {/* Footer / Action Buttons */}
                        <div className="mt-8 flex justify-between pt-6 border-t border-secondary-200 dark:border-secondary-700">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handlePrevious}
                                disabled={isLoading}
                                className="px-6 py-3 bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border border-secondary-300 dark:border-secondary-600 font-medium rounded-xl shadow-sm hover:bg-secondary-50 dark:hover:bg-secondary-700 flex items-center gap-2 transition-all"
                            >
                                Back
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => formRef.current?.submitForm()}
                                disabled={isLoading}
                                className="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-xl shadow-lg shadow-primary-500/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Continue to Subscription</span>
                                    </>
                                )}
                            </motion.button>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}

export default ProfileBuildingPage
